/**
 * Booking hours — the network-wide window in which a callback can be booked.
 *
 * One tenant config (crm_tenant_settings.config.booking) drives BOTH paths:
 * the patient-facing /book/<token> grid only ever offers slots inside it, and
 * a recruiter booking an outside-hours time gets a confirm first. Closed days
 * are skipped entirely, so a Friday link rolls forward to Mon/Tue rather than
 * offering the weekend.
 *
 * Read is open to every CRM user (recruiters should see the hours they're
 * working to); the PUT is admin-only and 403s into the notice below, matching
 * the AgentSettings posture.
 *
 * Closed dates and the clinic address per site show only while the api says
 * `scheduler_supported` (checkpoint regression L2): a deploy rolls this page
 * before the api, and the previous api drops closed dates from a save without
 * a word and has no clinic-sites route. A save whose reply lacks the flag is
 * never reported as having stored the dates.
 */
import { useCallback, useEffect, useState } from 'react';
import { CalendarClock, Loader2, ShieldOff } from 'lucide-react';
import api from '../../../services/api';
import PageHeader, { RefreshButton } from '../../components/PageHeader';
import { fieldCls } from '../../components/filters';
import { primaryBtnCls } from '../../../ui/styles';
import { Alert } from '../../components/ui';
import ClosedDatesEditor from '../../components/scheduler/ClosedDatesEditor';
import ClinicAddressesCard from '../../components/scheduler/ClinicAddressesCard';
import {
  BOOKING_EXTRAS_PENDING, bookingExtrasSupported, bookingSaveBody, closedDatesDropped,
} from '../../components/scheduler/schedulerLogic';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface Booking {
  days: number[];
  day_start: string;
  day_end: string;
  window_days: number;
  slot_minutes: number;
  min_notice_minutes: number;
  token_ttl_days: number;
  recruiter_invite_email: boolean;
  invite_show_patient_name: boolean;
  timezone: string;
  /** Holidays (owner decision 10): tenant-local 'YYYY-MM-DD' dates no grid offers. */
  closed_dates?: string[];
}

/**
 * Row-shaped switch — label + hint on the left, the pill on the right. Same
 * control as the study Portal/Website tabs so every CRM settings surface reads
 * as one system.
 */
function ToggleRow({ label, hint, checked, onChange }: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{label}</p>
        <p className="mt-0.5 text-xs leading-tight text-gray-500 dark:text-gray-400">{hint}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
          checked ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-150 ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}

const errMsg = (e: any): string =>
  e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Request failed';

/** Compact numeric knob — drafts locally so a half-typed number never saves. */
function NumField({ id, label, hint, value, onChange, min, max, disabled }: {
  id: string;
  label: string;
  hint?: string;
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  const commit = () => {
    const n = parseInt(draft, 10);
    if (!Number.isFinite(n) || n < min || n > max) { setDraft(String(value)); return; }
    if (n !== value) onChange(n);
  };
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-xs font-medium text-gray-600 dark:text-gray-300">{label}</label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className={`${fieldCls} mt-0.5 w-full tabular-nums`}
      />
      {hint && <p className="mt-1 text-[11px] leading-tight text-gray-500 dark:text-gray-400">{hint}</p>}
    </div>
  );
}

export default function BookingSettings() {
  const [booking, setBooking] = useState<Booking | null>(null);
  const [hoursLabel, setHoursLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const [dirty, setDirty] = useState(false);
  // The closed dates go in the save only once edited, so a save that never
  // touched them writes exactly the keys it wrote before.
  const [closedDirty, setClosedDirty] = useState(false);
  // Closed dates and the clinic address per site: only while the api stores
  // them (fails closed until a read or save says so; checkpoint L2).
  const [extrasSupported, setExtrasSupported] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/settings/booking');
      setBooking(res.data?.booking || null);
      setHoursLabel(res.data?.hours_label || '');
      setExtrasSupported(bookingExtrasSupported(res.data));
      setDirty(false);
      setClosedDirty(false);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (next: Partial<Booking>) => {
    setBooking((prev) => (prev ? { ...prev, ...next } : prev));
    setDirty(true);
    setNotice(null);
  };

  const toggleDay = (d: number) => {
    if (!booking) return;
    const set = new Set(booking.days);
    if (set.has(d)) set.delete(d); else set.add(d);
    patch({ days: Array.from(set).sort((a, b) => a - b) });
  };

  const save = async () => {
    if (!booking || saving) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const body = bookingSaveBody(booking, closedDirty, extrasSupported);
      const res = await api.put('/api/crm/settings/booking', body);
      setBooking(res.data?.booking || booking);
      setHoursLabel(res.data?.hours_label || hoursLabel);
      setExtrasSupported(bookingExtrasSupported(res.data));
      setDirty(false);
      setClosedDirty(false);
      if (closedDatesDropped('closed_dates' in body, res.data)) {
        // The previous api answered: it saved the rest and dropped the dates.
        setError('The other booking settings were saved, but the closed dates were not: the update is still finishing. Reload this page in a few minutes and add them again.');
      } else {
        setNotice('Booking settings saved.');
      }
    } catch (e: any) {
      if (e?.response?.status === 403) setDenied(true);
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const noDays = !!booking && booking.days.length === 0;
  const badWindow = !!booking && booking.day_end <= booking.day_start;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Booking hours"
        icon={CalendarClock}
        subtitle="When a callback can be booked, and what the recruiter gets when one is."
        actions={<RefreshButton onClick={load} loading={loading} />}
      />

      {denied && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <ShieldOff className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>Only a CRM admin can change booking settings. Your changes were not saved.</span>
        </div>
      )}
      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}
      {notice && (
        <Alert tone="info" icon={false}>{notice}</Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
        </div>
      ) : !booking ? null : (
        <>
          <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Open days</h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              No callback is offered or booked on a closed day.
            </p>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {DAY_LABELS.map((label, d) => {
                const on = booking.days.includes(d);
                return (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleDay(d)}
                    className={`inline-flex min-h-[44px] w-14 items-center justify-center rounded-lg border text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 sm:py-1.5 ${
                      on
                        ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                        : 'border-gray-300 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {noDays && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">Pick at least one open day.</p>
            )}

            <h2 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">Open hours</h2>
            <div className="mt-2 flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="day_start" className="block text-xs font-medium text-gray-600 dark:text-gray-300">Opens</label>
                <input
                  id="day_start"
                  type="time"
                  value={booking.day_start}
                  onChange={(e) => patch({ day_start: e.target.value })}
                  className={`${fieldCls} mt-0.5 w-32 tabular-nums`}
                />
              </div>
              <div>
                <label htmlFor="day_end" className="block text-xs font-medium text-gray-600 dark:text-gray-300">Closes</label>
                <input
                  id="day_end"
                  type="time"
                  value={booking.day_end}
                  onChange={(e) => patch({ day_end: e.target.value })}
                  className={`${fieldCls} mt-0.5 w-32 tabular-nums`}
                />
              </div>
              <p className="pb-1.5 text-xs text-gray-500 dark:text-gray-400">
                {booking.timezone.replace(/_/g, ' ')}
              </p>
            </div>
            {badWindow && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">Closing time must be after opening time.</p>
            )}
            {!noDays && !badWindow && hoursLabel && !dirty && (
              <p className="mt-2.5 text-xs text-gray-500 dark:text-gray-400">
                Currently bookable: <span className="font-medium text-gray-700 dark:text-gray-200">{hoursLabel}</span>.
                The last slot starts before closing, so no call runs past it.
              </p>
            )}

            <h2 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">Closed dates</h2>
            {extrasSupported ? (
              <>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Holidays and other days the office is closed. No callback is offered on them, by the patient&apos;s booking
                  page or by the texting agent&apos;s scheduling assistant. The list stays empty until you add dates.
                </p>
                <div className="mt-2.5">
                  <ClosedDatesEditor
                    value={booking.closed_dates || []}
                    timezone={booking.timezone}
                    onChange={(next) => { patch({ closed_dates: next }); setClosedDirty(true); }}
                  />
                </div>
              </>
            ) : (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{BOOKING_EXTRAS_PENDING}</p>
            )}
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Slot shape</h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              How the open hours are cut up on the patient's "pick a time" page.
            </p>
            <div className="mt-2.5 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <NumField
                id="slot_minutes" label="Slot length (min)" min={10} max={240}
                value={booking.slot_minutes} onChange={(n) => patch({ slot_minutes: n })}
              />
              <NumField
                id="window_days" label="Open days shown" min={1} max={30}
                hint="Counts open days — weekends are skipped, not spent."
                value={booking.window_days} onChange={(n) => patch({ window_days: n })}
              />
              <NumField
                id="min_notice_minutes" label="Min notice (min)" min={0} max={10080}
                hint="Earliest a patient may book from now."
                value={booking.min_notice_minutes} onChange={(n) => patch({ min_notice_minutes: n })}
              />
              <NumField
                id="token_ttl_days" label="Link valid (days)" min={1} max={90}
                hint="Lifetime of a texted booking link."
                value={booking.token_ttl_days} onChange={(n) => patch({ token_ttl_days: n })}
              />
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Calendar invite</h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              What the assigned recruiter gets by email when a call lands on their CRM calendar.
            </p>
            <div className="mt-1.5 divide-y divide-gray-100 dark:divide-gray-700">
              <ToggleRow
                label="Email the recruiter a calendar invite"
                hint="Sent from SiteOperations with an .ics attached and an Add to Google Calendar button. Rescheduling moves the event; cancelling removes it."
                checked={booking.recruiter_invite_email}
                onChange={(v) => patch({ recruiter_invite_email: v })}
              />
              <ToggleRow
                label="Put the patient's name in the event title"
                hint="Off by default — the invite is copied into Google or Outlook and syncs to phones, so the title stays de-identified. The email itself always names the patient."
                checked={booking.invite_show_patient_name}
                onChange={(v) => patch({ invite_show_patient_name: v })}
              />
            </div>
          </section>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty || noDays || badWindow}
              className={primaryBtnCls}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Saving…' : 'Save booking settings'}
            </button>
            {dirty && !saving && (
              <span className="text-xs text-gray-500 dark:text-gray-400">Unsaved changes</span>
            )}
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            A recruiter scheduling a call themselves can still book outside these hours, but only after
            confirming — the exception is recorded on the lead's timeline.
          </p>

          <ClinicAddressesCard supported={extrasSupported} />
        </>
      )}
    </div>
  );
}
